import { ElementType, ReactNode } from "react";
import { clsx } from "clsx";
import Panel from "@/common/Panel/Panel.tsx";

type CardHeader = {
    title: ReactNode;
    /**
     * The card's face: one picture of what it is about, in the accent colour
     * before the title.
     */
    icon?: ElementType;
    /** Red is for a card whose rows cannot be undone. */
    tone?: "indigo" | "red";
    /** Where the whole card stands, beside its title: on or off, a plan. */
    badge?: ReactNode;
    /** One or two sentences under the title, at a reading measure. */
    description?: ReactNode;
    /** Sits level with the title, on the right: a status, a link, a button. */
    action?: ReactNode;
};

/**
 * A card of a settings page and its header. `FormSection` is this with rows
 * in it; use the card itself for the part of a form that is not rows, such as
 * a result.
 */
export function FormCard({
    title,
    icon: Icon,
    tone = "indigo",
    badge,
    description,
    action,
    flush = false,
    surface = false,
    children,
}: CardHeader & {
    /**
     * Starts the body at the card's edge instead of under the title's text.
     * For rows, whose icons stand in the column the card's icon is in.
     */
    flush?: boolean;
    /**
     * Gives the card a fill of its own. Cards are transparent and take the
     * page's white; on a page with a patterned background that pattern would
     * show through the form.
     */
    surface?: boolean;
    children?: ReactNode;
}) {
    const card = (
        <Panel>
            {/* The card's own header, not the panel's: the panel sets its
                summary beside the action, so a status appearing there would
                squeeze the summary onto a second line and push every row
                down. Here the summary sits under that line at a reading
                measure of its own, and the action stays level with the
                title. */}
            <div className={"mb-6"}>
                <div className={"flex items-center justify-between gap-4"}>
                    <h2
                        className={
                            "flex items-center gap-2 text-lg font-semibold leading-6 tracking-tight text-default"
                        }
                    >
                        {/* No tile around it: the icon stands in the column
                            the rows' icons are in, and the title starts where
                            their labels do. */}
                        {Icon && (
                            <Icon
                                aria-hidden={"true"}
                                className={clsx(
                                    "size-5 shrink-0",
                                    tone === "red"
                                        ? "text-red-600 dark:text-red-400"
                                        : "text-indigo-500 dark:text-indigo-400",
                                )}
                            />
                        )}
                        {title}
                        {badge}
                    </h2>
                    {action && <div className={"shrink-0"}>{action}</div>}
                </div>
                {description && (
                    <div
                        className={clsx(
                            "max-w-3xl text-pretty text-sm text-subtle",
                            "mt-1",
                            // Under the title, not under the icon.
                            Icon && "pl-7",
                        )}
                    >
                        {description}
                    </div>
                )}
            </div>
            {Icon && !flush ? (
                <div className={"pl-7"}>{children}</div>
            ) : (
                children
            )}
        </Panel>
    );

    // The panel sets its own transparent fill, so the surface goes around it,
    // at the panel's radius.
    return surface ? (
        <div className={"rounded-xl bg-white dark:bg-zinc-900"}>{card}</div>
    ) : (
        card
    );
}

/**
 * A card of settings: the heading with a short summary, then one row per
 * thing, a faint dashed rule between them.
 */
export function FormSection({
    children,
    ...header
}: CardHeader & {
    /** See `FormCard`. */
    surface?: boolean;
    /** `FormRow`s, or a `FormEmpty`. */
    children: ReactNode;
}) {
    return (
        <FormCard {...header} flush>
            <FormRows>{children}</FormRows>
        </FormCard>
    );
}

/**
 * The rows of a card and the rules between them. `FormSection` is the usual
 * way in; use this in a `FormCard` that holds something above its rows.
 */
export function FormRows({ children }: { children: ReactNode }) {
    return (
        // Fainter than the card's own hairline: the rows are one group, and
        // the rule only keeps them apart.
        <div
            className={
                "divide-y divide-dashed divide-zinc-950/5 border-t border-dashed border-zinc-950/5 dark:divide-white/5 dark:border-white/5"
            }
        >
            {children}
        </div>
    );
}
